import { match, matchPath } from 'react-router'
import { createSelector } from 'reselect'
import { creditCardOption } from 'src/components/CreditCardDropdown/CreditCardDropdown'
import {
  UpdateVirtualCardFormRouteProps,
  updateVirtualCardFormRouteProps,
} from 'src/navigation/routeProps'
import { RootState } from 'src/redux/createRootReducer'
import { CreditCardsFullPaginatedDataState } from 'src/redux/creditCardsFullPaginatedData.reducer'
import { selectCreditCardListModelsFromState } from 'src/redux/selectors/data/creditCards.selectors'
import { selectReceiptAttachmentsListModels } from 'src/redux/selectors/data/receiptAttachments.selectors'
import { selectUserListModelsFromState } from 'src/redux/selectors/data/users.selectors'
import {
  selectVirtualCardPermissionsModelsFromState,
  selectVirtualCardSecureModels,
} from 'src/redux/selectors/data/virtualCards.selectors'
import { selectLocationFromState } from 'src/redux/selectors/router.selectors'
import { ErrorDetailMap } from 'src/utils/error.utils'

export const selectVirtualCardIdFromRoute = createSelector(
  [selectLocationFromState],
  (location) => {
    const mtch = matchPath<UpdateVirtualCardFormRouteProps>(
      location.pathname,
      updateVirtualCardFormRouteProps,
    ) as match<UpdateVirtualCardFormRouteProps>
    return mtch.params.id
  },
)

export const selectVirtualCardPermissions = createSelector(
  [selectVirtualCardPermissionsModelsFromState, selectVirtualCardIdFromRoute],
  (virtualCardPermissions, virtualCardId) => {
    return virtualCardPermissions[virtualCardId] !== undefined
      ? virtualCardPermissions[virtualCardId]
      : undefined
  },
)

export const selectVirtualCard = createSelector(
  [selectVirtualCardSecureModels, selectVirtualCardIdFromRoute],
  (virtualCards, virtualCardId) => {
    return virtualCards[virtualCardId] !== undefined
      ? virtualCards[virtualCardId]
      : undefined
  },
)

export const selectVirtualCardRecipient = createSelector(
  [selectVirtualCard, selectUserListModelsFromState],
  (virtualCard, users) => {
    return virtualCard !== undefined
      ? users[virtualCard.recipientId]
      : undefined
  },
)

export const selectVirtualCardCardholder = createSelector(
  [selectVirtualCard, selectUserListModelsFromState],
  (virtualCard, users) => {
    return virtualCard !== undefined
      ? users[virtualCard.cardholderId]
      : undefined
  },
)

const selectReceiptAttachmentIdsFromState = (state: RootState): string[] =>
  state.app.updateVirtualCard.receiptAttachmentIds

export const selectReceiptAttachmentModelsList = createSelector(
  [selectReceiptAttachmentIdsFromState, selectReceiptAttachmentsListModels],
  (ids, receiptAttachments) =>
    Object.keys(receiptAttachments).length > 0
      ? ids.map((id) => receiptAttachments[id])
      : [],
)

export const selectUploadingAttachmentsFromState = (
  state: RootState,
): Record<string, string> =>
  state.app.updateVirtualCard.uploadingAttachmentThumbnails

export const selectPuttingVirtualCardFromState = (state: RootState): boolean =>
  state.app.updateVirtualCard.puttingVirtualCard

export const selectPuttingVirtualCardCancelFromState = (
  state: RootState,
): boolean => state.app.updateVirtualCard.puttingVirtualCardCancel

export const selectTopLevelErrorFromState = (
  state: RootState,
): string | undefined => state.app.updateVirtualCard.topLevelError

export const selectErrorDetailMapFromState = (
  state: RootState,
): ErrorDetailMap<string> | undefined =>
  state.app.updateVirtualCard.errorDetailMap

export const selectPuttingVirtualCardRejectFromState = (
  state: RootState,
): boolean => state.app.updateVirtualCard.puttingVirtualCardReject

export const selectPuttingVirtualCardFailureFromState = (
  state: RootState,
): boolean => state.app.updateVirtualCard.puttingVirtualCardFailure

export const selectPuttingVirtualCardCancelUpdateFromState = (
  state: RootState,
): boolean => state.app.updateVirtualCard.puttingVirtualCardCancelUpdate

export const selectCreditCardsFullPaginatedData = (
  state: RootState,
): CreditCardsFullPaginatedDataState =>
  state.app.updateVirtualCard.creditCardsFullPaginatedData
const selectUniqueCreditCardIds = createSelector(
  [selectCreditCardsFullPaginatedData],
  (data) => data.ids.filter((id, i) => data.ids.indexOf(id) === i),
)
const selectCreditCardOptions = createSelector(
  [selectUniqueCreditCardIds, selectCreditCardListModelsFromState],
  (ids, creditCards) => ids.map((id) => creditCardOption(creditCards[id])),
)
export const selectFilteredCreditCardOptions = createSelector(
  [selectCreditCardOptions],
  (options) => options.filter(() => true),
)

export const selectRecurringEnabledCreditCardsFullPaginatedData = (
  state: RootState,
): CreditCardsFullPaginatedDataState =>
  state.app.updateVirtualCard.recurringEnabledCreditCardsFullPaginatedData
const selectUniqueRecurringEnabledCreditCardIds = createSelector(
  [selectRecurringEnabledCreditCardsFullPaginatedData],
  (data) => data.ids.filter((id, i) => data.ids.indexOf(id) === i),
)
const selectUniqueRecurringEnabledCreditCardOptions = createSelector(
  [
    selectUniqueRecurringEnabledCreditCardIds,
    selectCreditCardListModelsFromState,
  ],
  (ids, creditCards) => ids.map((id) => creditCardOption(creditCards[id])),
)
export const selectFilteredRecurringEnabledCreditCardOptions = createSelector(
  [selectUniqueRecurringEnabledCreditCardOptions],
  (options) =>
    options.filter((option) => option.details?.creditCard.features.recurrence),
)

export const selectGuestAccessEnabledCreditCardsFullPaginatedData = (
  state: RootState,
): CreditCardsFullPaginatedDataState =>
  state.app.updateVirtualCard.guestAccessEnabledCreditCardsFullPaginatedData
const selectUniqueGuestAccessEnabledCreditCardIds = createSelector(
  [selectGuestAccessEnabledCreditCardsFullPaginatedData],
  (data) => data.ids.filter((id, i) => data.ids.indexOf(id) === i),
)
const selectUniqueGuestAccessEnabledCreditCardOptions = createSelector(
  [
    selectUniqueGuestAccessEnabledCreditCardIds,
    selectCreditCardListModelsFromState,
  ],
  (ids, creditCards) => ids.map((id) => creditCardOption(creditCards[id])),
)
export const selectFilteredGuestAccessEnabledCreditCardOptions = createSelector(
  [selectUniqueGuestAccessEnabledCreditCardOptions],
  (options) =>
    options.filter((option) => option.details?.creditCard.features.direct),
)

export const selectBalanceLockEnabledCreditCardsFullPaginatedData = (
  state: RootState,
): CreditCardsFullPaginatedDataState =>
  state.app.updateVirtualCard.balanceLockedEnabledCreditCardsFullPaginatedData
const selectUniqueBalanceLockEnabledCreditCardIds = createSelector(
  [selectBalanceLockEnabledCreditCardsFullPaginatedData],
  (data) => data.ids.filter((id, i) => data.ids.indexOf(id) === i),
)
const selectUniqueBalanceLockEnabledCreditCardOptions = createSelector(
  [
    selectUniqueBalanceLockEnabledCreditCardIds,
    selectCreditCardListModelsFromState,
  ],
  (ids, creditCards) => ids.map((id) => creditCardOption(creditCards[id])),
)
export const selectFilteredBalanceLockEnabledCreditCardOptions = createSelector(
  [selectUniqueBalanceLockEnabledCreditCardOptions],
  (options) => options.filter(() => true),
)
